/**
 * Role Management Component
 *
 * Comprehensive role management with permissions
 * Features:
 * - Create/Edit/Delete roles
 * - Assign permissions to roles
 * - View users assigned to each role
 * - Role hierarchies
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Divider,
  Checkbox,
  Row,
  Col,
  Badge,
  Descriptions,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SafetyOutlined,
  UserOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { get_roles, add_role, delete_role } from '@/utils/api';
import { accessControlUtils } from '@/components/AccessControlUtils';
import TableContainer from '@/components/TableContainer';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';

const { Option } = Select;
const { TextArea } = Input;

// Available permissions (customize based on your system)
const AVAILABLE_PERMISSIONS = [
  { category: "User Management", permissions: ["create_user", "edit_user", "delete_user", "view_users"] },
  { category: "BMR Management", permissions: ["create_bmr", "edit_bmr", "approve_bmr", "release_bmr", "view_bmr"] },
  { category: "E-Signature", permissions: ["sign_documents", "view_signatures", "verify_signatures"] },
  { category: "Configuration", permissions: ["manage_roles", "manage_departments", "manage_designations", "system_config"] },
  { category: "Reports", permissions: ["view_reports", "export_reports", "create_reports"] },
  { category: "Quality Control", permissions: ["qc_approve", "qc_review", "qc_reject"] },
];

const RoleManagement = () => {
  const { isDark } = useTheme();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  const access = accessControlUtils("User Management");

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      await get_roles((res) => {
        if (res.data && res.data.data) {
          setRoles(res.data.data);
        }
      });
    } catch (error) {
      toast.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRole(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    form.setFieldsValue({
      role_name: role.role,
      description: role.description,
      permissions: role.permissions || [],
      is_active: role.is_active !== false,
    });
    setModalVisible(true);
  };

  const handleDelete = async (roleId) => {
    try {
      await delete_role({ id: roleId }, (res) => {
        if (res.status === "success") {
          toast.success("Role deleted successfully");
          loadRoles();
        } else {
          toast.error(res.message || "Failed to delete role");
        }
      });
    } catch (error) {
      toast.error("Failed to delete role");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const roleData = {
        role_name: values.role_name,
        description: values.description,
        permissions: values.permissions || [],
        is_active: values.is_active !== false,
        ...(editingRole && { id: editingRole.role_id }),
      };

      await add_role(roleData, (res) => {
        if (res.status === "success") {
          toast.success(editingRole ? "Role updated successfully" : "Role created successfully");
          setModalVisible(false);
          form.resetFields();
          loadRoles();
        } else {
          toast.error(res.message || "Failed to save role");
        }
      });
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const filteredRoles = roles.filter(role =>
    role.role?.toLowerCase().includes(searchText.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Role Name",
      dataIndex: "role",
      key: "role",
      width: "20%",
      render: (text, record) => (
        <Space>
          <SafetyOutlined style={{ color: "#1890ff" }} />
          <strong>{text}</strong>
          {!record.is_active && <Tag color="red">Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (text) => text || <span style={{ color: "#999" }}>No description</span>,
    },
    {
      title: "Permissions",
      key: "permissions",
      width: "25%",
      render: (_, record) => {
        const permissions = record.permissions || [];
        const displayPermissions = permissions.slice(0, 3);
        const remainingCount = permissions.length - 3;

        return (
          <Space wrap>
            {displayPermissions.map((perm, i) => (
              <Tag key={i} color="blue" style={{ fontSize: 11 }}>
                {perm.replace(/_/g, " ")}
              </Tag>
            ))}
            {remainingCount > 0 && (
              <Tooltip title={permissions.slice(3).join(", ")}>
                <Tag color="default">+{remainingCount} more</Tag>
              </Tooltip>
            )}
            {permissions.length === 0 && (
              <span style={{ color: "#999" }}>No permissions</span>
            )}
          </Space>
        );
      },
    },
    {
      title: "Users",
      key: "users",
      width: "10%",
      align: "center",
      render: (_, record) => (
        <Tooltip title="Number of users with this role">
          <Badge count={record.user_count || 0} showZero>
            <UserOutlined style={{ fontSize: 18, color: "#1890ff" }} />
          </Badge>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "15%",
      align: "center",
      render: (_, record) => (
        <Space>
          {access?.update_access && (
            <Tooltip title="Edit Role">
              <Button
                type="link"
                icon={<Icons.Pencil className="text-amber-600 w-5 h-5 cursor-pointer hover:text-amber-800" />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {access?.delete_access && (
            <Popconfirm
              title="Delete this role?"
              description="Users with this role will lose their permissions."
              onConfirm={() => handleDelete(record.role_id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{
                className: "bg-red-600 text-white hover:bg-red-700",
              }}
            >
              <Tooltip title="Delete Role">
                <Button type="link" danger icon={<Icons.Trash2 className="text-red-600 w-5 h-5 cursor-pointer hover:text-red-800" />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      className="border border-gray-300 shadow-md"
      style={{ backgroundColor: cardBgColor }}
      extra={
        <Space>
          <Input
            placeholder="Search roles..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
          {access?.create_access && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                borderColor: isDark ? '#0F172A' : defaultColors.primary,
              }}
            >
              Add Role
            </Button>
          )}
        </Space>
      }
    >
      <TableContainer
        className="role-management-table"
        data={filteredRoles}
        columns={columns}
        rowKey="role_id"
        loading={loading}
        pagination={{
          pageSize: 10,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `Total ${total} roles`,
        }}
        size="middle"
        height="calc(100vh - 555px)"
      />
      {/* Page-specific pagination-bar spacing — not something TableContainer covers generically. */}
      <style>
        {`
          .role-management-table .ant-table-pagination {
            margin-top: 16px !important;
          }
        `}
      </style>

      <Modal
        title={
          <Space>
            <SafetyOutlined />
            {editingRole ? "Edit Role" : "Create New Role"}
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSubmit}
        width={700}
        okText={editingRole ? "Update" : "Create"}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="role_name"
            label="Role Name"
            rules={[
              { required: true, message: "Please enter role name" },
              { min: 3, message: "Role name must be at least 3 characters" },
            ]}
          >
            <Input placeholder="e.g., Production Manager, QC Analyst" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Describe the responsibilities and purpose of this role..."
            />
          </Form.Item>

          <Divider>Permissions</Divider>

          <Form.Item
            name="permissions"
            label="Select Permissions"
          >
            <Checkbox.Group style={{ width: "100%" }}>
              {AVAILABLE_PERMISSIONS.map((category, idx) => (
                <div key={idx} style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: "#1890ff" }}>
                    {category.category}
                  </div>
                  <Row>
                    {category.permissions.map((perm) => (
                      <Col span={12} key={perm} style={{ marginBottom: 8 }}>
                        <Checkbox value={perm}>
                          {perm.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            name="is_active"
            valuePropName="checked"
          >
            <Checkbox>Active</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default RoleManagement;
